// Compare Plots View - Multi-cell 1D → 2D → 3D visualization
import { signal } from '@preact/signals'
import { useEffect, useState } from 'preact/hooks'
import { batchGetMinimalCellData } from '../../../modules/dataService.js'
import { compareCells, hiddenCells } from '../../../state/filterStore.js'
import { activeCompareView } from '../../../state/globalSignals.js'
import DimensionViewMultiCell from '../plots/DimensionViewMultiCell.jsx'
import EnergyDetailViewMultiCell from '../plots/EnergyDetailViewMultiCell.jsx'

// Compare-specific signals
export const comparePlotCategory = signal('electrical') // 'electrical' | 'thermal' | 'energy'
export const compareSubCategory = signal('energy') // depends on category

// Color palette for up to 5 cells
export const COMPARE_COLORS = [
  '#ea5b0c', // Orange (Batemo primary)
  '#0d7c99', // Cyan (Batemo accent)
  '#88a2aa', // Gray-blue
  '#ff8c42', // Light orange
  '#0a4d68'  // Dark cyan
]

// SVG Icons for main categories
const ElectricalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
)

const ThermalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
  </svg>
)

const EnergyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 12h8"></path>
    <path d="M12 8v8"></path>
  </svg>
)

// Main categories
const MAIN_CATEGORIES = [
  { id: 'electrical', label: 'Electrical', icon: <ElectricalIcon /> },
  { id: 'thermal', label: 'Thermal', icon: <ThermalIcon /> },
  { id: 'energy', label: 'Energy Analysis', icon: <EnergyIcon /> }
]

// Sub-categories per main category
const SUB_CATEGORIES = {
  electrical: [
    { id: 'energy', label: 'Energy' },
    { id: 'power', label: 'Power' },
    { id: 'voltage', label: 'Voltage' },
    { id: 'ac-impedance', label: 'AC Impedance' },
    { id: 'dc-resistance', label: 'DCIR' }
  ],
  thermal: [
    { id: 'heat', label: 'Heat Loss' },
    { id: 'temperature', label: 'Temperature' },
    { id: 'efficiency', label: 'Efficiency' }
  ],
  energy: [
    { id: 'ragone', label: 'Energy vs Power' }
  ]
}

export default function ComparePlotsView() {
  const activeView = activeCompareView.value // '2d' or '3d' from global state
  const [cellDataMap, setCellDataMap] = useState({}) // productId -> cellData
  const [loading, setLoading] = useState(true)
  const [collapsedCategories, setCollapsedCategories] = useState({}) // Track which categories are collapsed

  // Load cell data for all compare cells
  useEffect(() => {
    async function loadCells() {
      const cellIds = compareCells.value
      if (!cellIds || cellIds.length === 0) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // ⚠️ DEAD COMPONENT (not rendered in the active Compare dispatch; only its
        // COMPARE_COLORS const is imported elsewhere). batchGetMinimalCellData uses
        // the UN-GATED detail path — if this component is ever re-activated for the
        // Compare surface, it MUST fetch gated data for external DEMO users (pass a
        // fieldGating='demo' equivalent, mirroring WS 1.4b), or it will leak premium
        // measured metrics. See demo_field_policy / getCellNormalized({fieldGating}).
        const dataMap = await batchGetMinimalCellData(cellIds, { concurrency: 5 })
        setCellDataMap(dataMap)
      } catch (err) {
        console.error('[ComparePlotsView] Error loading cells:', err)
      }
      setLoading(false)
    }

    loadCells()
  }, [compareCells.value])

  // Auto-adjust view based on category (voltage has no 3D)
  useEffect(() => {
    const cat = comparePlotCategory.value
    const subCat = compareSubCategory.value

    // Voltage has no 3D - force 2D
    if (cat === 'electrical' && subCat === 'voltage' && activeView === '3d') {
      activeCompareView.value = '2d'
    }
  }, [comparePlotCategory.value, compareSubCategory.value])

  // When switching main category, reset sub-category
  useEffect(() => {
    const cat = comparePlotCategory.value
    const validSubs = SUB_CATEGORIES[cat]
    if (!validSubs.find(s => s.id === compareSubCategory.value)) {
      compareSubCategory.value = validSubs[0].id
    }
  }, [comparePlotCategory.value])

  const activeMainCat = MAIN_CATEGORIES.find(c => c.id === comparePlotCategory.value)
  const activeSubCats = SUB_CATEGORIES[comparePlotCategory.value] || []
  const activeSubCat = activeSubCats.find(c => c.id === compareSubCategory.value)

  // Toggle category collapse
  const toggleCategory = (catId) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }))
  }

  // Convert cellDataMap to array for rendering
  const allCellDataArray = Object.entries(cellDataMap).map(([productId, data]) => ({
    productId,
    ...data
  }))

  // Filter out hidden cells
  const cellDataArray = allCellDataArray.filter(cell => !hiddenCells.value.includes(cell.productId))

  if (loading) {
    return (
      <div className="compare-plots-view loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading cell data...</p>
        </div>
      </div>
    )
  }

  if (allCellDataArray.length === 0) {
    return (
      <div className="compare-plots-view empty">
        <div className="empty-state">
          <p>No cells selected for comparison</p>
          <p className="hint">Select 2-5 cells to compare plots</p>
        </div>
      </div>
    )
  }

  if (cellDataArray.length === 0) {
    return (
      <div className="compare-plots-view empty">
        <div className="empty-state">
          <p>All cells are hidden</p>
          <p className="hint">Use the eye icon in the compare panel to show cells</p>
        </div>
      </div>
    )
  }

  return (
    <div className="compare-plots-view">
      {/* Left Sidebar: Hierarchical Category Navigation */}
      <aside className="compare-plots-nav">
        <div className="nav-header">
          <h3>Plot Categories</h3>
          <div className="nav-subtitle">{cellDataArray.length} cells selected</div>
        </div>

        {/* Hierarchical Categories with Collapsible Sub-Categories */}
        <div className="hierarchical-nav">
          {MAIN_CATEGORIES.map(cat => {
            const isActive = comparePlotCategory.value === cat.id
            const isCollapsed = collapsedCategories[cat.id] && !isActive
            const subCats = SUB_CATEGORIES[cat.id] || []

            return (
              <div key={cat.id} className={`nav-category ${isActive ? 'active' : ''}`}>
                {/* Category Header - Collapsible */}
                <button
                  className="category-header"
                  onClick={() => {
                    comparePlotCategory.value = cat.id
                    // Auto-expand when selecting
                    if (collapsedCategories[cat.id]) {
                      toggleCategory(cat.id)
                    }
                  }}
                >
                  <span className="cat-icon">{cat.icon}</span>
                  <span className="cat-label">{cat.label}</span>
                  <button
                    className={`collapse-toggle ${isCollapsed ? 'collapsed' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleCategory(cat.id)
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                </button>

                {/* Sub-Categories - Collapsible */}
                {!isCollapsed && (
                  <div className="sub-category-list">
                    {subCats.map(sub => (
                      <button
                        key={sub.id}
                        className={`sub-category-btn ${compareSubCategory.value === sub.id && isActive ? 'active' : ''}`}
                        onClick={() => {
                          comparePlotCategory.value = cat.id
                          compareSubCategory.value = sub.id
                        }}
                      >
                        <span className="sub-indicator">•</span>
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="compare-plots-content">
        {/* Simplified Header - No controls (handled by UnifiedContextBar) */}
        <div className="compare-plots-header">
          <h2 className="plot-title">{activeSubCat?.label || 'Select a category'}</h2>
          <p className="plot-subtitle">
            Comparing {cellDataArray.length} cells • {activeView === '2d' ? '2D' : '3D'} view
          </p>
        </div>

        {/* Plot Area - Render based on category */}
        <div className="compare-plots-area">
          {comparePlotCategory.value === 'electrical' && (
            <DimensionViewMultiCell
              cellDataArray={cellDataArray}
              category={compareSubCategory.value}
              dimension={activeView}
            />
          )}

          {comparePlotCategory.value === 'thermal' && (
            <DimensionViewMultiCell
              cellDataArray={cellDataArray}
              category={compareSubCategory.value}
              dimension={activeView}
            />
          )}

          {comparePlotCategory.value === 'energy' && (
            <EnergyDetailViewMultiCell
              cellDataArray={cellDataArray}
            />
          )}
        </div>

        {/* Legend: Show all cells with hidden state styling */}
        <div className="compare-plots-legend">
          <div className="legend-title">Cells:</div>
          <div className="legend-items">
            {allCellDataArray.map((cell, idx) => {
              const isHidden = hiddenCells.value.includes(cell.productId)
              return (
                <div key={cell.productId} className={`legend-item ${isHidden ? 'legend-hidden' : ''}`}>
                  <span
                    className="legend-color"
                    style={`background: ${COMPARE_COLORS[idx % COMPARE_COLORS.length]}; opacity: ${isHidden ? 0.3 : 1}`}
                  ></span>
                  <span className="legend-label">
                    {cell.manufacturer} {cell.name}
                    {isHidden && <span className="legend-hidden-badge"> (hidden)</span>}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
